<template>
  <div class="pdf-viewer-container">
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-full bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900">
      <div class="text-center">
        <div class="mb-6">
          <RefreshCw class="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto" />
        </div>
        
        <div class="space-y-2">
          <p class="text-lg font-medium text-gray-800 dark:text-gray-200">
            Carregando PDF...
          </p>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex items-center justify-center h-full bg-gradient-to-br from-red-50 to-pink-100 dark:from-gray-800 dark:to-gray-900">
      <div class="text-center">
        <div class="mb-6">
          <div class="w-20 h-20 mx-auto relative">
            <div class="absolute inset-0 rounded-full bg-gradient-to-r from-red-400 to-pink-500 opacity-20 animate-pulse"></div>
            <div class="absolute inset-2 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center">
              <AlertTriangle class="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>
        
        <div class="space-y-3">
          <p class="text-lg font-medium text-gray-800 dark:text-gray-200 flex items-center justify-center gap-2">
            <XCircle class="w-5 h-5 text-red-600 dark:text-red-400" />
            Erro ao carregar PDF
          </p>
          <p class="text-sm text-red-600 dark:text-red-400 max-w-md mx-auto">
            {{ error }}
          </p>
        </div>
        
        <button 
          @click="loadPDF"
          class="mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center gap-2 mx-auto transition-colors"
        >
          <RefreshCw class="w-4 h-4" />
          Tentar Novamente
        </button>
      </div>
    </div>

    <!-- PDF Viewer -->
    <div v-else class="pdf-viewer-wrapper">


   
      <!-- PDF Viewer -->
      <div class="pdf-canvas-container" ref="canvasContainer">
        <iframe 
          v-if="useIframe"
          :src="pdfUrl" 
          class="pdf-iframe"
          @load="onIframeLoad"
          @error="onIframeError"
        ></iframe>
        <canvas v-else ref="pdfCanvas" class="pdf-canvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { AlertTriangle, XCircle, RefreshCw } from 'lucide-vue-next';

export default {
  name: 'PDFViewer',
  props: {
    pdfUrl: {
      type: String,
      required: true
    },
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  emits: ['loaded', 'error', 'page-changed'],
  setup(props, { emit }) {
    // Refs
    const canvasContainer = ref(null);
    const pdfCanvas = ref(null);

    // Estado
    const loading = ref(false);
    const error = ref('');
    const pdfDoc = ref(null);
    const currentPage = ref(1);
    const totalPages = ref(0);
    const scale = ref(1.0);
    const renderTask = ref(null);
    const pdfjsLib = ref(null);
    const useIframe = ref(true); // Usar iframe por padrão

    // Constantes
    const MIN_SCALE = 0.5;
    const MAX_SCALE = 3.0;
    const SCALE_STEP = 0.25;

    // Carregar PDF.js dinamicamente
    const loadPDFJS = async () => {
      if (!pdfjsLib.value) {
        try {
          // Usar CDN em vez de import local para evitar problemas de build
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
          document.head.appendChild(script);
          
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
          });
          
          // Configurar worker
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
          pdfjsLib.value = window.pdfjsLib;
        } catch (err) {
          console.error('Erro ao carregar PDF.js:', err);
          throw new Error('Não foi possível carregar o visualizador de PDF');
        }
      }
      return pdfjsLib.value;
    };

    // Carregar PDF
    const loadPDF = async () => {
      if (!props.pdfUrl) {
        return;
      }
      
      loading.value = true;
      error.value = '';
      
      try {
        // Verificar se a URL está acessível
        const response = await fetch(props.pdfUrl, {
          method: 'GET',
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Erro HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Verificar se há conteúdo
        const contentLength = response.headers.get('content-length');
        if (contentLength && parseInt(contentLength) === 0) {
          throw new Error('O PDF gerado está vazio');
        }
        
        // Se estamos usando iframe, não precisamos carregar PDF.js
        if (useIframe.value) {
          emit('loaded', {
            totalPages: 1,
            currentPage: 1
          });
          return;
        }
        
        // Se não estiver usando iframe, carregar PDF.js
        const pdfjs = await loadPDFJS();
        
        // Cancelar renderização anterior se existir
        if (renderTask.value) {
          renderTask.value.cancel();
        }
        
        // Carregar o PDF
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout ao carregar PDF (30s)')), 30000);
        });
        
        const loadingTask = pdfjs.getDocument({
          url: props.pdfUrl,
          withCredentials: true,
          httpHeaders: {
            'Accept': 'application/pdf'
          }
        });
        
        // Usar Promise.race para adicionar timeout
        pdfDoc.value = await Promise.race([loadingTask.promise, timeoutPromise]);
        
        totalPages.value = pdfDoc.value.numPages;
        currentPage.value = 1;
        
        // Aguardar um pouco para garantir que o canvas esteja pronto
        await nextTick();
        
        // Renderizar primeira página
        await renderPage();
        
        emit('loaded', {
          totalPages: totalPages.value,
          currentPage: currentPage.value
        });
        
      } catch (err) {
        console.error('PDFViewer: Erro ao carregar PDF:', err);
        error.value = `Erro ao carregar o PDF: ${err.message}`;
        emit('error', err);
        
        // Se falhar, tentar usar iframe como fallback
        useIframe.value = true;
      } finally {
        loading.value = false;
      }
    };

    // Renderizar página
    const renderPage = async () => {
      if (!pdfDoc.value || !pdfCanvas.value) {
        return;
      }
      
      // Verificar se o canvas está visível
      const canvas = pdfCanvas.value;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
        return renderPage(); // Tentar novamente
      }
      
      try {
        // Cancelar renderização anterior
        if (renderTask.value) {
          renderTask.value.cancel();
        }
        
        // Obter página
        const page = await pdfDoc.value.getPage(currentPage.value);
        
        // Calcular viewport
        const viewport = page.getViewport({ scale: scale.value });
        
        // Configurar canvas
        const canvas = pdfCanvas.value;
        const context = canvas.getContext('2d');
        
        // Garantir que o canvas tenha dimensões mínimas
        const minWidth = 800;
        const minHeight = 600;
        
        canvas.width = Math.max(viewport.width, minWidth);
        canvas.height = Math.max(viewport.height, minHeight);
        
        // Limpar o canvas antes de renderizar
        context.clearRect(0, 0, canvas.width, canvas.height);
        
        // Renderizar página
        renderTask.value = page.render({
          canvasContext: context,
          viewport: viewport
        });
        
        await renderTask.value.promise;
        
        emit('page-changed', {
          currentPage: currentPage.value,
          totalPages: totalPages.value
        });
        
      } catch (err) {
        if (err.name === 'RenderingCancelled') {
          // Renderização foi cancelada, ignorar
          return;
        }
        console.error('PDFViewer: Erro ao renderizar página:', err);
        error.value = `Erro ao renderizar página: ${err.message}`;
      }
    };

    // Navegação
    const previousPage = async () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        await renderPage();
      }
    };

    const nextPage = async () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        await renderPage();
      }
    };

    // Zoom
    const zoomIn = async () => {
      if (scale.value < MAX_SCALE) {
        scale.value = Math.min(MAX_SCALE, scale.value + SCALE_STEP);
        await renderPage();
      }
    };

    const zoomOut = async () => {
      if (scale.value > MIN_SCALE) {
        scale.value = Math.max(MIN_SCALE, scale.value - SCALE_STEP);
        await renderPage();
      }
    };



    // Callbacks do iframe
    const onIframeLoad = () => {
      loading.value = false;
      emit('loaded', {
        totalPages: 1,
        currentPage: 1
      });
    };

    const onIframeError = (event) => {
      console.error('PDFViewer: Erro no iframe:', event);
      error.value = 'Erro ao carregar o PDF no iframe';
      loading.value = false;
      emit('error', new Error('Erro no iframe'));
    };

    // Watchers
    watch(() => props.pdfUrl, (newUrl) => {
      if (newUrl && props.autoLoad) {
        loadPDF();
      }
    });

    // Lifecycle
    onMounted(() => {
      if (props.autoLoad && props.pdfUrl) {
        nextTick(() => {
          loadPDF();
        });
      }
    });

    onUnmounted(() => {
      // Cancelar renderização em andamento
      if (renderTask.value) {
        renderTask.value.cancel();
      }
      
      // Limpar recursos
      if (pdfDoc.value) {
        pdfDoc.value.destroy();
      }
    });

    return {
      // Refs
      canvasContainer,
      pdfCanvas,
      
      // Estado
      loading,
      error,
      currentPage,
      totalPages,
      scale,
      useIframe,
      
      // Métodos
      loadPDF,
      previousPage,
      nextPage,
      zoomIn,
      zoomOut,
      onIframeLoad,
      onIframeError,
      
      // Ícones
      AlertTriangle,
      XCircle,
      RefreshCw
    };
  }
};
</script>

<style scoped>
.pdf-viewer-container {
  width: 100%;
  height: 100%;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.pdf-viewer-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.pdf-canvas-container {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #f5f5f5;
  min-height: 600px;
  width: 100%;
  height: 100%;
}

.pdf-canvas {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background: white;
  border-radius: 4px;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.pdf-iframe {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background: white;
  border-radius: 4px;
  width: 100%;
  height: 100%;
  min-height: 600px;
  border: none;
  flex: 1;
  display: block;
}

/* Dark mode */
.dark .pdf-viewer-container {
  background: #1f2937;
}

.dark .pdf-canvas-container {
  background: #1f2937;
}

.dark .pdf-canvas {
  background: white;
}

/* Garantir que o container ocupe todo o espaço */
.pdf-viewer-container {
  position: relative;
}

.pdf-canvas-container {
  position: relative;
}

/* Animação de rotação suave para o ícone de refresh */
@keyframes spin-smooth {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin-smooth 1s linear infinite;
}
</style> 